// src/pages/admin/MobileAdminDashboard.jsx
// Native app-like mobile admin for quotations + bills (super_admin role).
// Catalog/design management stays desktop-only — this shell focuses on:
//   • browsing all quotations / bills (reuses useQuotations hook)
//   • creating & editing quotations (reuses QuotationBuilder / CreateQuotationTab)
//   • profile / logout / switch-to-desktop

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";
import { fetchDesigns, subscribeCustomers, subscribeTransports } from "../../firebase/services";
import { B, fonts, Btn, Spinner, useConfirmModal } from "../../components/UI";
import useQuotations from "../../hooks/useQuotations";
import CreateQuotationTab from "./tabs/CreateQuotationTab";
import MobileAllQuotationsTab from "./tabs/MobileAllQuotationsTab";
import MobileBookingsTab from "./tabs/MobileBookingsTab";
import QuotationBuilder from "./forms/QuotationBuilder";
import InstallBanner from "../../components/InstallBanner";

// ─── Icons ───────────────────────────────────────────────────────────────────
const IconQuotations = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h9l4 4v14H6z" />
    <path d="M15 3v4h4" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="14" y2="16" />
  </svg>
);

const IconBookings = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <circle cx="12" cy="15" r="1.4" />
    <circle cx="16" cy="15" r="1.4" />
    <circle cx="8" cy="15" r="1.4" />
  </svg>
);

const IconProfile = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const IconPlus = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// ─── Bottom Nav ───────────────────────────────────────────────────────────────
function BottomNav({ active, onChange }) {
  return (
    <nav className="bottom-nav" style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
      background: B.headerBg, borderTop: "1px solid rgba(255,255,255,0.08)",
      display: "flex", alignItems: "center", height: 64,
      boxShadow: "0 -4px 24px rgba(0,0,0,0.5)",
    }}>
      <NavButton id="quotations" active={active} onChange={onChange} label="Quotations">
        <IconQuotations color={active === "quotations" ? B.orange : "#94a3b8"} />
      </NavButton>

      <NavButton id="bookings" active={active} onChange={onChange} label="Bookings">
        <IconBookings color={active === "bookings" ? B.orange : "#94a3b8"} />
      </NavButton>

      {/* Center New button */}
      <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <button onClick={() => onChange("create")}
          style={{
            width: 54, height: 54, borderRadius: "50%", border: "none",
            background: active === "create"
              ? `linear-gradient(135deg, ${B.orangeLight}, ${B.orange})`
              : `linear-gradient(135deg, ${B.orange}, ${B.orangeDark})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", marginBottom: 20,
            boxShadow: `0 4px 20px ${B.orange}99, 0 0 0 4px ${B.headerBg}, 0 0 0 5px ${B.orange}44`,
            transform: active === "create" ? "scale(1.08)" : "scale(1)",
            transition: "all 0.2s",
          }}>
          <IconPlus />
        </button>
      </div>

      <NavButton id="profile" active={active} onChange={onChange} label="Profile">
        <IconProfile color={active === "profile" ? B.orange : "#94a3b8"} />
      </NavButton>
    </nav>
  );
}

function NavButton({ id, active, onChange, label, children }) {
  const isActive = active === id;
  return (
    <button onClick={() => onChange(id)}
      style={{
        flex: 1, border: "none", background: "none",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", gap: 4, padding: "8px 0 6px", cursor: "pointer",
      }}
      onTouchStart={e => e.currentTarget.style.opacity = "0.7"}
      onTouchEnd={e => e.currentTarget.style.opacity = "1"}
    >
      <div style={{ width: isActive ? 20 : 0, height: 3, background: B.orange, borderRadius: 2, marginBottom: 2, transition: "width 0.25s", position: "absolute", top: 0 }} />
      {children}
      <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 400, color: isActive ? B.orange : "#94a3b8", fontFamily: fonts.body, letterSpacing: "0.03em", transition: "color 0.2s" }}>
        {label}
      </span>
    </button>
  );
}

// ─── Profile Screen ───────────────────────────────────────────────────────────
function ProfileScreen({ profile, quotations, onLogout, onSwitchToDesktop }) {
  const billCount = quotations.filter(q => q.bill_number).length;
  const quoteCount = quotations.length - billCount;
  const stats = [
    { label: "Quotations", value: quoteCount, color: B.orange, icon: "🗒️" },
    { label: "Bills", value: billCount, color: B.green, icon: "🧾" },
  ];
  return (
    <div style={{ padding: "20px 18px 120px" }}>
      <div style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 16, padding: 20, marginBottom: 16, textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: B.orangeFaint, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 10px" }}>
          {(profile?.name || "A").charAt(0).toUpperCase()}
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: B.text, fontFamily: fonts.adminDisplay }}>{profile?.name || "Admin"}</div>
        <div style={{ fontSize: 12, color: B.textMid, marginTop: 4, fontFamily: fonts.body }}>Super Admin</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        {stats.map(({ label, value, color, icon }) => (
          <div key={label} style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: 16, borderLeft: `4px solid ${color}` }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color, fontFamily: fonts.adminDisplay }}>{value}</div>
            <div style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>{label}</div>
          </div>
        ))}
      </div>

      <button onClick={onSwitchToDesktop}
        style={{ width: "100%", background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 14, padding: 14, fontSize: 14, fontWeight: 600, color: B.orange, cursor: "pointer", fontFamily: fonts.body, marginBottom: 12 }}>
        🖥️ Switch to Desktop View
      </button>
      <button onClick={onLogout}
        style={{ width: "100%", background: B.redLight, border: `1.5px solid ${B.red}33`, borderRadius: 14, padding: 14, fontSize: 14, fontWeight: 600, color: B.red, cursor: "pointer", fontFamily: fonts.body }}>
        🚪 Logout
      </button>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function MobileToast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{ position: "fixed", top: 70, left: 16, right: 16, zIndex: 999, background: toast.color || B.orange, color: "#fff", padding: "13px 18px", borderRadius: 12, fontWeight: 600, fontSize: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.3)", animation: "fadeUp 0.3s", fontFamily: fonts.body, textAlign: "center" }}>
      {toast.msg}
    </div>
  );
}

// ─── MAIN MOBILE ADMIN DASHBOARD ──────────────────────────────────────────────
export default function MobileAdminDashboard({ onSwitchToDesktop }) {
  const { user, profile, permissions, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [screen, setScreen] = useState("quotations"); // quotations | bookings | create | profile
  const [designs, setDesigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [pendingCrudOps, setPendingCrudOps] = useState(0);
  const { confirm, confirmModal } = useConfirmModal();

  const showToast = (msg, color = B.orange) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2800);
  };
  const setCrudBusy = isBusy => setPendingCrudOps(prev => isBusy ? prev + 1 : Math.max(0, prev - 1));
  const isCrudBusy = pendingCrudOps > 0;

  const q = useQuotations({
    user, profile, permissions, branding, designs, customers,
    showToast, setCrudBusy, confirm,
  });

  const {
    quotations, quotationNumbers, quotationSettings, quotationSaving, whatsappSending,
    viewQuotation, setViewQuotation, editQuotation, setEditQuotation,
    quotationPrintPrompt, handlePrintPromptClose,
    quotationSearch, setQuotationSearch,
    quotationCustomerFilter, setQuotationCustomerFilter,
    quotationTypeFilter, setQuotationTypeFilter,
    quotationDateFrom, setQuotationDateFrom,
    quotationDateTo, setQuotationDateTo,
    quotationPage, setQuotationPage,
    filteredQuotations, quotationTotalPages,
    handleSaveQuotation, handleDeleteQuotation, handlePrintQuotation, handlePrintTransportSlip,
    handleSendWhatsAppFromList,
    handleSendUpiQrFromList,
    canManageQuotations,
    formatDateTime, formatCurrency,
  } = q;

  // When the "Auto-fetch design info in quotation" setting is off we skip loading
  // the design catalogue (see the design-loading effect below).
  const fetchDesignDetails = Boolean(quotationSettings?.fetchDesignDetails);

  // Designs are read-only in this mobile admin shell (the quotation builder uses
  // them for the dropdown / print). No permanent live listener is needed here -
  // fetch the catalogue once with a getDocs and cache it, matching the scoped
  // treatment on the desktop dashboard.
  const designsLoadedRef = useRef(false);
  useEffect(() => {
    // Only load the heavy (~500 doc) design catalogue when the "Auto-fetch design
    // info in quotation" setting is enabled. When disabled we skip the read, so
    // quotation creation never pulls the design list.
    if (designsLoadedRef.current || !fetchDesignDetails) {
      setLoading(false);
      return;
    }
    setLoading(true);
    let active = true;
    (async () => {
      let list;
      try { list = await fetchDesigns(); }
      catch (e) { if (active) setLoading(false); return; }
      if (active) {
        designsLoadedRef.current = true;
        setDesigns(list);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [fetchDesignDetails]);

  useEffect(() => {
    const u2 = subscribeCustomers(setCustomers);
    const u3 = subscribeTransports(setTransports);
    return () => { u2(); u3(); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin-login");
  };

  // Navigate back to the Quotations tab after a quotation/bill is successfully
  // created or updated. Returns the underlying success flag so a failed save
  // keeps the user on the create/edit form with validation toasts shown.
  const handleMobileSaveQuotation = async (payload) => {
    const ok = await handleSaveQuotation(payload);
    if (ok) setScreen("quotations");
    return ok;
  };

  return (
    <div style={{ minHeight: "100vh", background: B.cream, fontFamily: fonts.body }}>
      <MobileToast toast={toast} />
      <InstallBanner />
      {confirmModal}

      {screen === "quotations" && (
        <MobileAllQuotationsTab
          quotations={quotations}
          customers={customers}
          designs={designs}
          filteredQuotations={filteredQuotations}
          quotationSearch={quotationSearch}
          setQuotationSearch={setQuotationSearch}
          quotationCustomerFilter={quotationCustomerFilter}
          setQuotationCustomerFilter={setQuotationCustomerFilter}
          quotationTypeFilter={quotationTypeFilter}
          setQuotationTypeFilter={setQuotationTypeFilter}
          quotationDateFrom={quotationDateFrom}
          setQuotationDateFrom={setQuotationDateFrom}
          quotationDateTo={quotationDateTo}
          setQuotationDateTo={setQuotationDateTo}
          viewQuotation={viewQuotation}
          setViewQuotation={setViewQuotation}
          setEditQuotation={setEditQuotation}
          setTab={setScreen}
          handleSaveQuotation={handleSaveQuotation}
          quotationSaving={quotationSaving}
          handleDeleteQuotation={handleDeleteQuotation}
          canManageQuotations={canManageQuotations}
          handlePrintQuotation={handlePrintQuotation}
          handlePrintTransportSlip={handlePrintTransportSlip}
          handleSendWhatsApp={handleSendWhatsAppFromList}
          handleSendUpiQr={handleSendUpiQrFromList}
          formatDateTime={formatDateTime}
          formatCurrency={formatCurrency}
          quotationPage={quotationPage}
          quotationTotalPages={quotationTotalPages}
          setQuotationPage={setQuotationPage}
          QuotationBuilder={QuotationBuilder}
          quotationSettings={quotationSettings}
        />
      )}

      {screen === "create" && (
        <div style={{ paddingBottom: 120 }}>
          <CreateQuotationTab
            customers={customers}
            transports={transports}
            designs={designs}
            handleSaveQuotation={handleMobileSaveQuotation}
            quotationSaving={quotationSaving}
            editQuotation={editQuotation}
            setEditQuotation={setEditQuotation}
            QuotationBuilder={QuotationBuilder}
            quotationSettings={quotationSettings}
            quotations={quotationNumbers}
            showToast={showToast}
            confirm={confirm}
            onBack={() => { setEditQuotation(null); setScreen("quotations"); }}
          />
        </div>
      )}

      {screen === "profile" && (
        <ProfileScreen
          profile={profile}
          quotations={quotations}
          onLogout={handleLogout}
          onSwitchToDesktop={onSwitchToDesktop}
        />
      )}

      {screen === "bookings" && (
        <MobileBookingsTab showToast={showToast} />
      )}

      {isCrudBusy && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(255, 255, 255, 0.82)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(1px)" }}>
          <Spinner size={120} />
        </div>
      )}

      <BottomNav active={screen} onChange={setScreen} />

      {whatsappSending && (
        <div style={{ position: "fixed", bottom: 80, left: 16, right: 16, zIndex: 12000, background: "#fff", border: "1px solid #25D366", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)", fontFamily: fonts.body, fontSize: 13, color: B.text }}>
          <span style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid #25D366", borderTopColor: "transparent", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
          <span>Sending WhatsApp message…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {quotationPrintPrompt && (
        <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, background: "rgba(20, 16, 12, 0.45)", zIndex: 11000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "100%", maxWidth: 420, background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 16, boxShadow: "0 24px 48px rgba(0, 0, 0, 0.18)", padding: "22px 22px 18px" }}>
            <h3 style={{ margin: 0, fontSize: 20, color: B.text, fontFamily: fonts.adminDisplay }}>Quotation saved successfully</h3>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: B.textMid, lineHeight: 1.6, fontFamily: fonts.body }}>
              Do you want to print quotation <strong>#{quotationPrintPrompt.quotation_number || "—"}</strong> now?
            </p>
            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Btn variant="ghost" onClick={handlePrintPromptClose}>Not now</Btn>
              <Btn variant="primary" onClick={() => { handlePrintQuotation(quotationPrintPrompt); handlePrintPromptClose(); }}>
                Print now
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
